import { useCallback, useEffect, useRef, useState } from "react";

export const CATALOG_SEARCH_DEBOUNCE_MS = 300;
export const CATALOG_SEARCH_MIN_CHARS = 1;

/**
 * Debounced async catalog search with in-flight request cancellation.
 *
 * @param {object} options
 * @param {(query: string, ctx?: { signal?: AbortSignal }) => Promise<Array<unknown>>} options.searchFn
 * @param {number} [options.debounceMs=300]
 * @param {number} [options.minChars=1] Minimum typed length before remote search (0 = allow empty for defaults)
 * @param {boolean} [options.fetchOnEmpty=false] Fetch suggestions when query is empty (e.g. on focus)
 */
export default function useDebouncedCatalogSearch({
  searchFn,
  debounceMs = CATALOG_SEARCH_DEBOUNCE_MS,
  minChars = CATALOG_SEARCH_MIN_CHARS,
  fetchOnEmpty = true,
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(false);
  const requestIdRef = useRef(0);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const emptyCacheRef = useRef(null);
  const searchFnRef = useRef(searchFn);

  useEffect(() => {
    searchFnRef.current = searchFn;
  }, [searchFn]);

  const cancelInFlight = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  const runSearch = useCallback(
    async (rawQuery, { useCache = true } = {}) => {
      const trimmed = String(rawQuery || "").trim();

      if (!trimmed && useCache && emptyCacheRef.current) {
        cancelInFlight();
        requestIdRef.current += 1;
        setResults(emptyCacheRef.current);
        setLoading(false);
        return;
      }

      if (!trimmed && !fetchOnEmpty) {
        cancelInFlight();
        setResults([]);
        setLoading(false);
        return;
      }

      if (trimmed.length > 0 && trimmed.length < minChars) {
        cancelInFlight();
        setResults([]);
        setLoading(false);
        return;
      }

      cancelInFlight();
      const controller = new AbortController();
      abortRef.current = controller;

      const reqId = ++requestIdRef.current;
      setLoading(true);
      try {
        const items =
          (await searchFnRef.current(trimmed, { signal: controller.signal })) ||
          [];
        if (reqId !== requestIdRef.current) return;
        if (!trimmed) emptyCacheRef.current = items;
        setResults(items);
      } catch (err) {
        if (err?.name === "AbortError") return;
        if (reqId !== requestIdRef.current) return;
        setResults([]);
      } finally {
        if (reqId === requestIdRef.current) {
          setLoading(false);
          if (abortRef.current === controller) abortRef.current = null;
        }
      }
    },
    [cancelInFlight, fetchOnEmpty, minChars],
  );

  const enable = useCallback(() => {
    setActive(true);
  }, []);

  const close = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    cancelInFlight();
    setActive(false);
    setLoading(false);
  }, [cancelInFlight]);

  const reset = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    cancelInFlight();
    requestIdRef.current += 1;
    setQuery("");
    setResults([]);
    setLoading(false);
    setActive(false);
    emptyCacheRef.current = null;
  }, [cancelInFlight]);

  useEffect(() => {
    if (!active) return undefined;

    const trimmed = String(query || "").trim();

    if (!trimmed && fetchOnEmpty && emptyCacheRef.current) {
      setResults(emptyCacheRef.current);
      setLoading(false);
      return undefined;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    const delay =
      !trimmed && fetchOnEmpty && !emptyCacheRef.current ? 0 : debounceMs;

    debounceRef.current = setTimeout(() => {
      runSearch(query, { useCache: true });
    }, delay);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, active, debounceMs, fetchOnEmpty, runSearch]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      cancelInFlight();
    },
    [cancelInFlight],
  );

  return {
    query,
    setQuery,
    results,
    loading,
    active,
    enable,
    close,
    reset,
    runSearch,
  };
}
